import { useState, useEffect, useCallback } from 'react';
import { useMobile } from '../hooks/useMobile';
import ImapSettings from '../components/orders/ImapSettings';

// ─── Shared bits ─────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  delivered: { bg: 'rgba(52,211,153,0.15)',  color: '#34d399' },
  shipped:   { bg: 'rgba(99,102,241,0.15)',  color: '#a5b4fc' },
  ordered:   { bg: 'rgba(251,191,36,0.15)',  color: '#fcd34d' },
  cancelled: { bg: 'rgba(248,113,113,0.15)', color: '#f87171' },
  refunded:  { bg: 'rgba(168,85,247,0.15)',  color: '#c084fc' },
  other:     { bg: 'rgba(100,116,139,0.15)', color: '#64748b' },
};

const STATUS_RANK = { cancelled: 5, refunded: 4, delivered: 3, shipped: 2, ordered: 1, other: 0 };

const CAT_STYLE = {
  business:  { color: '#818cf8', bg: 'rgba(99,102,241,0.12)' },
  personal:  { color: '#f87171', bg: 'rgba(248,113,113,0.10)' },
  discarded: { color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
};

const CARD = {
  background: '#1e293b',
  borderRadius: 14,
  border: '1px solid rgba(255,255,255,0.07)',
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.other;
  return (
    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color, textTransform: 'capitalize' }}>
      {status}
    </span>
  );
}

function CatChip({ category }) {
  const c = CAT_STYLE[category] || CAT_STYLE.discarded;
  return (
    <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: c.bg, color: c.color, textTransform: 'capitalize', letterSpacing: '0.3px' }}>
      {category}
    </span>
  );
}

function fmtDate(iso)      { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
function fmtDateShort(iso) { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); }
function parseAmount(str)  { const m = (str || '').match(/[\d.]+/); return m ? parseFloat(m[0]) : 0; }
function toInputDate(d)    { return d.toISOString().slice(0, 10); }

function snapshot(r) {
  return {
    date: r.date, amount: r.amount, retailer: r.retailer, item: r.item,
    status: r.status, account: r.account, orderId: r.orderId || null,
    source: r.source || 'email',
  };
}

// Split a date range into monthly batches to keep each API call under the timeout
function buildBatches(sinceStr, untilStr) {
  const since = new Date(sinceStr);
  const until = new Date(untilStr);
  until.setHours(23, 59, 59, 999);
  const batches = [];
  let cursor = new Date(since);
  while (cursor < until) {
    const next = new Date(cursor);
    next.setMonth(next.getMonth() + 1);
    const end = next < until ? next : until;
    batches.push({
      since: cursor.toISOString(),
      until: end.toISOString(),
      label: cursor.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
    });
    cursor = next;
  }
  return batches.length ? batches : [{ since: since.toISOString(), until: until.toISOString(), label: 'range' }];
}

// Merge a batch into the accumulated map, deduping across batches by orderId
function mergeInto(map, newReceipts) {
  newReceipts.forEach(r => {
    const key = r.orderId ? `order:${r.orderId}` : r.id;
    const prev = map.get(key);
    if (!prev) { map.set(key, r); return; }
    const base  = new Date(r.date) < new Date(prev.date) ? r : prev;
    const newer = base === r ? prev : r;
    map.set(key, {
      ...base,
      status: (STATUS_RANK[newer.status] || 0) > (STATUS_RANK[base.status] || 0) ? newer.status : base.status,
      amount: base.amount || newer.amount,
      emailCount: (r.emailCount || 1) + (prev.emailCount || 1),
      category: base.category || newer.category,
      autoClassified: base.autoClassified || newer.autoClassified,
    });
  });
}

// ─── Stats ───────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ ...CARD, flex: 1, minWidth: 130, padding: '14px 18px' }}>
      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || '#f1f5f9', letterSpacing: '-0.5px' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function StatsRow({ business }) {
  const now   = new Date();
  const ymNow = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const year  = String(now.getFullYear());

  let monthTotal = 0, ytdTotal = 0;
  business.forEach(e => {
    const d = e.date || e.at;
    if (!d) return;
    const v = parseAmount(e.amount);
    if (d.slice(0, 7) === ymNow) monthTotal += v;
    if (d.slice(0, 4) === year)  ytdTotal   += v;
  });

  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
      <StatCard label="This month" value={`£${monthTotal.toFixed(2)}`} sub={now.toLocaleDateString('en-GB', { month: 'long' })} accent="#818cf8" />
      <StatCard label="Year to date" value={`£${ytdTotal.toFixed(2)}`} sub={year} accent="#34d399" />
      <StatCard label="Business receipts" value={business.length} sub="all time" />
    </div>
  );
}

function MonthlyBreakdown({ business }) {
  const byMonth = {};
  business.forEach(e => {
    const d = e.date || e.at;
    if (!d) return;
    const key = d.slice(0, 7);
    byMonth[key] = (byMonth[key] || 0) + parseAmount(e.amount);
  });
  const months = Object.entries(byMonth).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 12);
  if (!months.length) return null;
  const max = Math.max(...months.map(([, v]) => v), 1);

  return (
    <div style={{ ...CARD, padding: '18px 22px', marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', marginBottom: 14 }}>Monthly business spend</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {months.map(([key, total]) => {
          const label = new Date(key + '-01').toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11, color: '#64748b', width: 52, flexShrink: 0 }}>{label}</span>
              <div style={{ flex: 1, height: 8, background: 'rgba(255,255,255,0.04)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(total / max) * 100}%`, borderRadius: 4, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)' }} />
              </div>
              <span style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600, width: 76, textAlign: 'right', flexShrink: 0 }}>£{total.toFixed(2)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Entry list (Recent / Bin) ───────────────────────────────────────────────

function EntryRow({ entry, actions, isMobile }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '10px 12px' : '11px 16px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{entry.retailer}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#818cf8' }}>{entry.amount || '—'}</span>
          <CatChip category={entry.category} />
          {entry.status && <StatusBadge status={entry.status} />}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.item}
        </div>
        <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
          {entry.date ? fmtDate(entry.date) : ''}{entry.at ? ` · sorted ${fmtDateShort(entry.at)}` : ''}{entry.orderId ? ` · #${entry.orderId}` : ''}
        </div>
      </div>
      {actions}
    </div>
  );
}

function RecentTab({ recent, business, isMobile }) {
  return (
    <>
      <MonthlyBreakdown business={business} />
      <div style={{ ...CARD, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px 10px', fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>
          Recently added <span style={{ color: '#475569', fontWeight: 400 }}>· last {recent.length} sorted</span>
        </div>
        {recent.length === 0
          ? <div style={{ padding: '24px 16px', fontSize: 12, color: '#475569', textAlign: 'center' }}>Nothing saved yet — run a scan and sort some receipts.</div>
          : recent.map(e => <EntryRow key={e.id} entry={e} isMobile={isMobile} />)}
      </div>
    </>
  );
}

function BinTab({ bin, onRestore, isMobile }) {
  return (
    <div style={{ ...CARD, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px 10px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>Bin</div>
        <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>Last {bin.length} discarded — restore anything binned by accident.</div>
      </div>
      {bin.length === 0
        ? <div style={{ padding: '24px 16px', fontSize: 12, color: '#475569', textAlign: 'center' }}>Bin is empty.</div>
        : bin.map(e => (
          <EntryRow key={e.id} entry={e} isMobile={isMobile} actions={
            <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexDirection: isMobile ? 'column' : 'row' }}>
              <button onClick={() => onRestore(e, 'business')} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 8, cursor: 'pointer', background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)' }}>→ Business</button>
              <button onClick={() => onRestore(e, 'personal')} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 8, cursor: 'pointer', background: 'rgba(248,113,113,0.10)', color: '#f87171', border: '1px solid rgba(248,113,113,0.22)' }}>→ Personal</button>
            </div>
          } />
        ))}
    </div>
  );
}

// ─── Rules panel ─────────────────────────────────────────────────────────────

function RulesPanel({ rules, onDelete }) {
  const entries = Object.entries(rules);
  if (!entries.length) {
    return <p style={{ fontSize: 12, color: '#475569', margin: 0 }}>No rules yet. Set them from the sort cards.</p>;
  }
  const style = rule =>
    rule === 'business'  ? { bg: 'rgba(99,102,241,0.12)',  border: 'rgba(99,102,241,0.25)',  color: '#818cf8' } :
    rule === 'personal'  ? { bg: 'rgba(248,113,113,0.10)', border: 'rgba(248,113,113,0.22)', color: '#f87171' } :
                           { bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.25)', color: '#94a3b8' };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {entries.map(([retailer, rule]) => {
        const s = style(rule);
        return (
          <div key={retailer} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px 5px 12px', borderRadius: 20, fontSize: 12, background: s.bg, border: `1px solid ${s.border}`, color: s.color }}>
            <span style={{ fontWeight: 600 }}>{retailer}</span>
            <span style={{ opacity: 0.6, fontSize: 11 }}>→ {rule}</span>
            <button onClick={() => onDelete(retailer)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.5, fontSize: 13, padding: '0 2px', lineHeight: 1 }}>×</button>
          </div>
        );
      })}
    </div>
  );
}

// ─── Sort card ───────────────────────────────────────────────────────────────

function SortCard({ receipt, current, total, anim, rules, onDecide, onSkip, onGoBack, canGoBack, onSetRule, isMobile }) {
  const existingRule = rules[receipt.retailer];
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>Sorting · {current} of {total}</div>
        {!isMobile && (
          <div style={{ fontSize: 11, color: '#334155', display: 'flex', gap: 6 }}>
            {[['B','business'],['P','personal'],['D','discard'],['S','skip'],['⌫','back']].map(([k,l]) => (
              <span key={k}>
                <kbd style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding: '1px 6px', color: '#475569', fontFamily: 'inherit' }}>{k}</kbd> {l}
              </span>
            ))}
          </div>
        )}
      </div>

      <div style={{ height: 3, background: 'rgba(255,255,255,0.05)', borderRadius: 4, marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 4, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', width: `${(current / total) * 100}%`, transition: 'width 0.3s ease' }} />
      </div>

      <div style={{
        ...CARD, borderRadius: 16, padding: isMobile ? '20px' : '32px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.3)', marginBottom: 16,
        transform: anim === 'left' ? 'translateX(-50px)' : anim === 'right' ? 'translateX(50px)' : 'translateX(0)',
        opacity: anim ? 0 : 1,
        transition: 'transform 0.2s ease, opacity 0.2s ease',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 11, color: '#475569', fontWeight: 500 }}>{receipt.account}</div>
            {receipt.emailCount > 1 && (
              <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', color: '#64748b', fontWeight: 600 }}>
                {receipt.emailCount} emails
              </span>
            )}
          </div>
          <StatusBadge status={receipt.status} />
        </div>

        <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: '#f1f5f9', marginBottom: 2 }}>{receipt.retailer}</div>
        <div style={{ fontSize: isMobile ? 36 : 52, fontWeight: 800, color: '#818cf8', letterSpacing: '-1.5px', lineHeight: 1, marginBottom: 16 }}>
          {receipt.amount || 'N/A'}
        </div>
        {receipt.orderId && (
          <div style={{ fontSize: 11, color: '#475569', fontFamily: 'monospace', marginBottom: 8 }}>#{receipt.orderId}</div>
        )}
        <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5, marginBottom: 8 }}>{receipt.item}</div>
        <div style={{ fontSize: 11, color: '#475569' }}>{fmtDate(receipt.date)} · {receipt.from}</div>

        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#475569' }}>Always classify <strong style={{ color: '#64748b' }}>{receipt.retailer}</strong> as:</span>
          {['business', 'personal', 'discarded'].map(cat => {
            const active = existingRule === cat;
            const c = CAT_STYLE[cat];
            return (
              <button key={cat} onClick={() => onSetRule(receipt.retailer, active ? null : cat)} style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 12, cursor: 'pointer',
                border: `1px solid ${active ? c.color : 'rgba(255,255,255,0.1)'}`,
                background: active ? c.bg : 'rgba(255,255,255,0.03)',
                color: active ? c.color : '#64748b',
                textTransform: 'capitalize',
              }}>{active ? '✓ ' : ''}{cat === 'discarded' ? 'discard' : cat}</button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
        <ActionBtn label="← Business" sublabel="(B)" color="#818cf8" bg="rgba(99,102,241,0.12)" hoverBg="rgba(99,102,241,0.22)" onClick={() => onDecide('business')} />
        <ActionBtn label="Personal →" sublabel="(P)" color="#f87171" bg="rgba(248,113,113,0.10)" hoverBg="rgba(248,113,113,0.20)" onClick={() => onDecide('personal')} />
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => onDecide('discarded')} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.08)', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          🗑 Discard (D)
        </button>
        <button onClick={onSkip} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#475569', fontSize: 12, cursor: 'pointer' }}>
          Skip (S)
        </button>
        <button onClick={onGoBack} disabled={!canGoBack} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid rgba(99,102,241,0.15)', background: 'rgba(99,102,241,0.06)', color: canGoBack ? '#818cf8' : '#334155', fontSize: 12, cursor: canGoBack ? 'pointer' : 'not-allowed' }}>
          ← Back (⌫)
        </button>
      </div>
    </div>
  );
}

function ActionBtn({ label, sublabel, color, bg, hoverBg, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{
      flex: 1, padding: '14px 0', borderRadius: 12, border: 'none',
      background: hover ? hoverBg : bg, color,
      fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'background 0.15s',
    }}>
      {label} <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.6 }}>{sublabel}</span>
    </button>
  );
}

// ─── Results table ───────────────────────────────────────────────────────────

function ResultsTable({ receipts, isMobile }) {
  const total = receipts.reduce((sum, r) => sum + parseAmount(r.amount), 0);
  return (
    <div style={{ ...CARD, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: isMobile ? '14px 14px 10px' : '16px 20px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#818cf8' }}>Business expenses</div>
        <div style={{ fontSize: 12, color: '#64748b' }}>{receipts.length} item{receipts.length !== 1 ? 's' : ''} · £{total.toFixed(2)}</div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
              {['Date', 'Price', 'Status', 'Order', 'Retailer', 'Item', 'Source'].map(h => (
                <th key={h} style={{ padding: isMobile ? '8px 10px' : '9px 14px', textAlign: 'left', fontWeight: 500, color: '#64748b', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {receipts.map((r, i) => (
              <tr key={r.id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDateShort(r.date)}</td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#f1f5f9', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.amount || '—'}</td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px' }}><StatusBadge status={r.status} /></td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#475569', fontFamily: 'monospace', fontSize: 10, whiteSpace: 'nowrap' }}>{r.orderId || '—'}</td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#e2e8f0', fontWeight: 500, whiteSpace: 'nowrap' }}>{r.retailer}</td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#94a3b8', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.item}</td>
                <td style={{ padding: isMobile ? '8px 10px' : '9px 14px', color: '#475569', whiteSpace: 'nowrap', fontSize: 11 }}>{r.account}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function ReceiptScanner() {
  const isMobile = useMobile();

  const [tab, setTab]             = useState('scanner'); // scanner | recent | bin
  const [phase, setPhase]         = useState('idle');    // idle | scanning | sorting | saving | done
  const [receipts, setReceipts]   = useState([]);
  const [errors, setErrors]       = useState([]);
  const [toSort, setToSort]       = useState([]);
  const [sortIdx, setSortIdx]     = useState(0);
  const [decisions, setDecisions] = useState({});
  const [cardAnim, setCardAnim]   = useState(null);
  const [scanProgress, setScanProgress] = useState(null);

  const [rules, setRules]         = useState({});
  const [showRules, setShowRules] = useState(false);
  const [showInboxes, setShowInboxes] = useState(false);
  const [inboxFallback, setInboxFallback] = useState(false);
  const [log, setLog]             = useState({ recent: [], bin: [], business: [] });

  // Custom date range — defaults to the last 6 months
  const [sinceDate, setSinceDate] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 6); return toInputDate(d); });
  const [untilDate, setUntilDate] = useState(() => toInputDate(new Date()));

  const currentId      = toSort[sortIdx];
  const currentReceipt = receipts.find(r => r.id === currentId);

  const fetchLog = useCallback(() => {
    fetch('/api/receipts/log').then(r => r.json()).then(d => { if (d.ok) setLog({ recent: d.recent, bin: d.bin, business: d.business }); }).catch(() => {});
  }, []);

  const fetchInboxStatus = useCallback(() => {
    fetch('/api/receipts/settings').then(r => r.json()).then(d => { if (d.ok) setInboxFallback(d.fallback); }).catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/api/receipts/rules').then(r => r.json()).then(d => { if (d.ok) setRules(d.rules); }).catch(() => {});
    fetchLog();
  }, [fetchLog]);

  // Refresh fallback status on mount and whenever the inbox panel is toggled
  // (covers deletions, which don't fire onSaved)
  useEffect(() => { fetchInboxStatus(); }, [fetchInboxStatus, showInboxes]);

  useEffect(() => {
    if (phase !== 'sorting' || tab !== 'scanner') return;
    function onKey(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'b' || e.key === 'B') decide('business');
      if (e.key === 'p' || e.key === 'P') decide('personal');
      if (e.key === 'd' || e.key === 'D') decide('discarded');
      if (e.key === 's' || e.key === 'S') skip();
      if (e.key === 'Backspace') goBack();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ── Scan (batched over the chosen date range) ──
  async function scan() {
    setPhase('scanning');
    setReceipts([]); setErrors([]); setDecisions({}); setToSort([]); setSortIdx(0);

    const batches     = buildBatches(sinceDate, untilDate);
    const accumulated = new Map();
    const allErrors   = [];

    for (let i = 0; i < batches.length; i++) {
      const { since, until, label } = batches[i];
      setScanProgress({ batch: i + 1, total: batches.length, label });
      try {
        const res  = await fetch('/api/receipts/scan', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ since, until }),
        });
        const data = await res.json();
        if (!data.ok) throw new Error(data.error);
        mergeInto(accumulated, data.receipts);
        allErrors.push(...(data.errors || []));
      } catch (err) {
        allErrors.push({ error: `${label}: ${err.message}` });
      }
    }

    const all = [...accumulated.values()].sort((a, b) => new Date(b.date) - new Date(a.date));
    const preDecisions = {};
    all.forEach(r => { if (r.category) preDecisions[r.id] = r.category; });
    const uncategorized = all.filter(r => !r.category).map(r => r.id);

    setDecisions(preDecisions);
    setReceipts(all);
    setErrors(allErrors.slice(0, 5));
    setToSort(uncategorized);
    setSortIdx(0);
    setScanProgress(null);
    setPhase(uncategorized.length > 0 ? 'sorting' : 'done');
  }

  // ── Rules ──
  async function setRule(retailer, rule) {
    try {
      const res  = await fetch('/api/receipts/rules', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ retailer, rule }) });
      const data = await res.json();
      if (data.ok) setRules(data.rules);
    } catch {}
  }

  // ── Sorting ──

  // Fire-and-forget: save a single decision immediately as it's made.
  // If the user goes back and re-decides, the second call overwrites the first.
  function saveOne(id, category) {
    const r = receipts.find(rec => rec.id === id);
    fetch('/api/receipts/categorize', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decisions: [{ id, orderId: r?.orderId || null, category, receipt: r ? snapshot(r) : null }] }),
    }).catch(() => {});
  }

  function decide(category) {
    if (cardAnim || !currentReceipt) return;
    saveOne(currentId, category);
    const dir = category === 'business' ? 'left' : category === 'personal' ? 'right' : null;
    if (dir) setCardAnim(dir);
    setTimeout(() => {
      const next = sortIdx + 1;
      setDecisions(prev => ({ ...prev, [currentId]: category }));
      setSortIdx(next);
      setCardAnim(null);
      if (next >= toSort.length) { fetchLog(); setPhase('done'); }
    }, dir ? 200 : 0);
  }

  function skip() {
    if (cardAnim) return;
    const next = sortIdx + 1;
    setSortIdx(next);
    if (next >= toSort.length) { fetchLog(); setPhase('done'); }
  }

  // Undo: go back to the previous card, remove its decision from local state.
  // If re-decided, saveOne fires again and overwrites the previous server save.
  function goBack() {
    if (cardAnim || sortIdx === 0) return;
    const prevId = toSort[sortIdx - 1];
    if (prevId && decisions[prevId]) {
      setDecisions(prev => { const n = { ...prev }; delete n[prevId]; return n; });
    }
    setSortIdx(s => s - 1);
  }

  // ── Bin restore ──
  async function restoreFromBin(entry, category) {
    try {
      await fetch('/api/receipts/categorize', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decisions: [{ id: entry.id, orderId: entry.orderId || null, category }] }),
      });
      fetchLog();
    } catch {}
  }

  // ── CSV export ──
  function downloadCsv() {
    const allDecisions = { ...decisions };
    receipts.forEach(r => { if (r.category && !allDecisions[r.id]) allDecisions[r.id] = r.category; });
    const business = receipts.filter(r => allDecisions[r.id] === 'business');
    const header   = ['Date','Price','Status','OrderID','Retailer','Item','Source','Category'].join(',');
    const rows     = business.map(r => [
      fmtDate(r.date), r.amount || '', r.status, r.orderId || '',
      r.retailer, `"${(r.item || '').replace(/"/g,'""')}"`, r.account, 'Business',
    ].join(','));
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `receipts_business_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  // ── Derived ──
  const allDecisions = { ...decisions };
  receipts.forEach(r => { if (r.category && !allDecisions[r.id]) allDecisions[r.id] = r.category; });
  const businessReceipts = receipts.filter(r => allDecisions[r.id] === 'business');
  const autoClassified   = receipts.filter(r => r.autoClassified).length;
  const isScanning       = phase === 'scanning' || phase === 'saving';

  const dateInputStyle = {
    background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
    color: '#e2e8f0', fontSize: 12, padding: '7px 10px', colorScheme: 'dark',
  };

  return (
    <div style={{ maxWidth: 860 }}>
      {/* ── Header ── */}
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, letterSpacing: '-0.5px', color: '#f1f5f9' }}>Receipt Scanner</h1>
        <p style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>Scans all connected inboxes for purchase receipts — sort into business and personal.</p>
      </div>

      {/* ── Stats ── */}
      <StatsRow business={log.business} />

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[['scanner', 'Scanner'], ['recent', 'Recent'], ['bin', `Bin${log.bin.length ? ` (${log.bin.length})` : ''}`]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            fontSize: 12, fontWeight: 600, padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: tab === key ? '#334155' : 'transparent',
            color: tab === key ? '#f1f5f9' : '#64748b',
          }}>{label}</button>
        ))}
      </div>

      {/* ── Recent tab ── */}
      {tab === 'recent' && <RecentTab recent={log.recent} business={log.business} isMobile={isMobile} />}

      {/* ── Bin tab ── */}
      {tab === 'bin' && <BinTab bin={log.bin} onRestore={restoreFromBin} isMobile={isMobile} />}

      {/* ── Scanner tab ── */}
      {tab === 'scanner' && (
        <>
          {/* Controls */}
          <div style={{ ...CARD, padding: isMobile ? '14px' : '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
              From <input type="date" value={sinceDate} onChange={e => setSinceDate(e.target.value)} style={dateInputStyle} />
            </label>
            <label style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
              To <input type="date" value={untilDate} onChange={e => setUntilDate(e.target.value)} style={dateInputStyle} />
            </label>

            <div style={{ flex: 1 }} />

            <button onClick={() => { setShowInboxes(s => !s); setShowRules(false); }} style={{ fontSize: 12, borderRadius: 10, cursor: 'pointer', padding: '8px 14px', background: showInboxes ? '#334155' : 'rgba(255,255,255,0.03)', color: showInboxes ? '#e2e8f0' : '#64748b', border: '1px solid rgba(255,255,255,0.08)' }}>
              📥 Inboxes
            </button>

            <button onClick={() => { setShowRules(s => !s); setShowInboxes(false); }} style={{ fontSize: 12, borderRadius: 10, cursor: 'pointer', padding: '8px 14px', background: showRules ? '#334155' : 'rgba(255,255,255,0.03)', color: showRules ? '#e2e8f0' : '#64748b', border: '1px solid rgba(255,255,255,0.08)' }}>
              ⚙ Rules{Object.keys(rules).length > 0 ? ` (${Object.keys(rules).length})` : ''}
            </button>

            {phase === 'done' && businessReceipts.length > 0 && (
              <button onClick={downloadCsv} style={{ fontSize: 12, fontWeight: 500, borderRadius: 10, cursor: 'pointer', border: '1px solid rgba(52,211,153,0.3)', padding: '8px 16px', background: 'rgba(52,211,153,0.1)', color: '#34d399' }}>
                ↓ CSV
              </button>
            )}

            <button onClick={scan} disabled={isScanning} style={{
              fontSize: 13, fontWeight: 600, borderRadius: 10, border: 'none', padding: '9px 20px',
              background: isScanning ? '#1e293b' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: isScanning ? '#475569' : '#fff',
              cursor: isScanning ? 'not-allowed' : 'pointer',
              boxShadow: isScanning ? 'none' : '0 2px 8px rgba(99,102,241,0.4)',
            }}>
              {phase === 'scanning' ? (scanProgress ? `Scanning ${scanProgress.label} (${scanProgress.batch}/${scanProgress.total})…` : 'Scanning…')
                : phase === 'saving' ? 'Saving…'
                : '↻ Scan'}
            </button>
          </div>

          {/* Inboxes panel */}
          {showInboxes && (
            <div style={{ ...CARD, padding: isMobile ? '16px' : '18px 22px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', marginBottom: 4 }}>Receipt Scanner inboxes</div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 14 }}>
                Separate from the Order Dashboard's inboxes.
                {inboxFallback && <span style={{ color: '#fcd34d' }}> Currently using the Order Dashboard's inboxes — add inboxes here to override.</span>}
              </div>
              <ImapSettings basePath="/api/receipts/settings" onSaved={fetchInboxStatus} />
            </div>
          )}

          {/* Rules panel */}
          {showRules && (
            <div style={{ ...CARD, padding: isMobile ? '16px' : '18px 22px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', marginBottom: 12 }}>Retailer rules</div>
              <RulesPanel rules={rules} onDelete={retailer => setRule(retailer, null)} />
            </div>
          )}

          {/* Scan progress */}
          {phase === 'scanning' && scanProgress && (
            <div style={{ ...CARD, padding: '14px 18px', marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                <span>Scanning <strong style={{ color: '#94a3b8' }}>{scanProgress.label}</strong></span>
                <span>{scanProgress.batch} / {scanProgress.total}</span>
              </div>
              <div style={{ height: 4, background: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 4, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', width: `${(scanProgress.batch / scanProgress.total) * 100}%`, transition: 'width 0.4s ease' }} />
              </div>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div style={{ background: 'rgba(220,38,38,0.12)', color: '#fca5a5', borderRadius: 10, padding: '11px 14px', fontSize: 12, marginBottom: 14, border: '1px solid rgba(220,38,38,0.25)', fontWeight: 500 }}>
              ⚠ {errors.map(e => e.account ? `${e.account}: ${e.error}` : e.error).join(' · ')}
            </div>
          )}

          {/* Auto-classified banner */}
          {autoClassified > 0 && (phase === 'sorting' || phase === 'done') && (
            <div style={{ background: 'rgba(99,102,241,0.08)', borderRadius: 10, border: '1px solid rgba(99,102,241,0.15)', padding: '10px 14px', fontSize: 12, marginBottom: 14, color: '#818cf8', fontWeight: 500 }}>
              ✓ {autoClassified} receipt{autoClassified !== 1 ? 's' : ''} auto-classified by retailer rules
            </div>
          )}

          {/* Sort cards */}
          {phase === 'sorting' && currentReceipt && (
            <SortCard
              receipt={currentReceipt}
              current={sortIdx + 1}
              total={toSort.length}
              anim={cardAnim}
              rules={rules}
              onDecide={decide}
              onSkip={skip}
              onGoBack={goBack}
              canGoBack={sortIdx > 0}
              onSetRule={setRule}
              isMobile={isMobile}
            />
          )}

          {/* Results */}
          {phase === 'done' && receipts.length > 0 && (
            businessReceipts.length > 0
              ? <ResultsTable receipts={businessReceipts} isMobile={isMobile} />
              : <div style={{ ...CARD, padding: '32px', textAlign: 'center', color: '#64748b', fontSize: 13, marginBottom: 16 }}>No receipts marked as Business this session.</div>
          )}

          {phase === 'done' && receipts.length === 0 && (
            <div style={{ ...CARD, padding: '32px', textAlign: 'center', color: '#64748b', fontSize: 13, marginBottom: 16 }}>No receipts found in this date range.</div>
          )}

          {/* Idle */}
          {phase === 'idle' && (
            <div style={{ ...CARD, padding: isMobile ? '48px 20px' : '64px 40px', textAlign: 'center' }}>
              <div style={{ fontSize: 44, marginBottom: 16 }}>🧾</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 10 }}>Scan your inboxes for receipts</div>
              <div style={{ fontSize: 13, color: '#64748b', maxWidth: 420, margin: '0 auto', lineHeight: 1.6 }}>
                Pick a date range above (defaults to the last 6 months) and hit <strong style={{ color: '#94a3b8' }}>Scan</strong>.
                Long ranges run in monthly batches automatically. Manage inboxes via the <strong style={{ color: '#94a3b8' }}>📥 Inboxes</strong> button.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
